import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  Input, Label, Textarea,
} from '@higgins/ui';
import { createApp, updateApp, type CatalogueApp, type TileFields } from '../lib/api';
import { TILE_COLOURS, TILE_ICONS, type TileColour } from '../lib/tile-art';
import { TileIcon } from './tile-icon';

const BLANK: TileFields = { name: '', description: '', url: 'https://', icon: 'clipboard-list', colour: 'orange', sortOrder: 100, stagingSite: false };

/** "Site File Sharing" → "site-file-sharing": a suggestion the admin can overwrite. */
function suggestKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
}

interface Props {
  /** null = add a new app. */
  editing: CatalogueApp | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Add or edit an app's tile. The server re-validates everything — notably the
 * link, which must be https:// on a higgins.com.au or azurestaticapps.net
 * address. New apps start in Dev; status and ticks live on the grid.
 */
export function AppFormDialog({ editing, open, onClose, onSaved }: Props) {
  const [fields, setFields] = useState<TileFields>(BLANK);
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFields(
      editing
        ? (({ name, description, url, icon, colour, sortOrder, stagingSite }) =>
            ({ name, description, url, icon, colour, sortOrder, stagingSite }))(editing)
        : BLANK,
    );
    setKey(editing?.key ?? '');
    setKeyTouched(false);
  }, [open, editing]);

  const set = <K extends keyof TileFields>(field: K, value: TileFields[K]) =>
    setFields((f) => ({ ...f, [field]: value }));

  function onName(name: string) {
    set('name', name);
    if (!editing && !keyTouched) setKey(suggestKey(name));
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) await updateApp(editing.key, fields);
      else await createApp(key, fields);
      toast.success(editing ? 'App updated' : 'App added', {
        description: editing ? undefined : 'It starts in Dev — only the Dev group sees it until you change its status.',
      });
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Could not save', { description: e instanceof Error ? e.message : 'Unknown error' });
    } finally {
      setSaving(false);
    }
  }

  const canSave = fields.name.trim() && fields.url.trim() && (editing || key) && !saving;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add an app'}</DialogTitle>
          <DialogDescription>
            How the app&apos;s tile looks and where it goes. Who sees it is set on the grid.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <TileIcon icon={fields.icon} colour={fields.colour} />
            <span className="min-w-0">
              <span className="block font-semibold text-slate-900">{fields.name || 'App name'}</span>
              <span className="mt-1 block text-sm text-slate-600">{fields.description || 'What it does, in one line.'}</span>
            </span>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="app-name">Name</Label>
            <Input id="app-name" value={fields.name} maxLength={100} onChange={(e) => onName(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="app-key">Key</Label>
            <Input
              id="app-key"
              value={key}
              disabled={!!editing}
              maxLength={100}
              onChange={(e) => {
                setKeyTouched(true);
                setKey(e.target.value);
              }}
            />
            <p className="text-xs text-slate-500">
              {editing
                ? 'Fixed once created — the app checks access by this key.'
                : 'Lower-case words joined by hyphens. The app itself must use the same key to check access.'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="app-description">Description</Label>
            <Textarea
              id="app-description"
              rows={2}
              maxLength={300}
              value={fields.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="app-url">Link</Label>
            <Input id="app-url" value={fields.url} maxLength={500} onChange={(e) => set('url', e.target.value)} />
            <p className="text-xs text-slate-500">https:// on a higgins.com.au or azurestaticapps.net address.</p>
          </div>

          <div className="space-y-1.5">
            <Label>Icon</Label>
            <div className="grid grid-cols-7 gap-1.5">
              {Object.entries(TILE_ICONS).map(([name, Icon]) => (
                <button
                  key={name}
                  type="button"
                  title={name}
                  aria-label={name}
                  aria-pressed={fields.icon === name}
                  onClick={() => set('icon', name)}
                  className={`flex h-9 items-center justify-center rounded-md border transition ${
                    fields.icon === name
                      ? 'border-orange-500 bg-orange-50 text-slate-900'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-6">
            <div className="space-y-1.5">
              <Label>Colour</Label>
              <div className="flex gap-2">
                {(Object.keys(TILE_COLOURS) as TileColour[]).map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={TILE_COLOURS[c].label}
                    aria-label={TILE_COLOURS[c].label}
                    aria-pressed={fields.colour === c}
                    onClick={() => set('colour', c)}
                    className={`h-8 w-8 rounded-full ring-offset-2 transition ${fields.colour === c ? 'ring-2 ring-slate-900' : ''}`}
                    style={{ backgroundColor: TILE_COLOURS[c].hex }}
                  />
                ))}
              </div>
            </div>
            <div className="w-28 space-y-1.5">
              <Label htmlFor="app-order">Order</Label>
              <Input
                id="app-order"
                type="number"
                min={0}
                max={9999}
                value={fields.sortOrder}
                onChange={(e) => set('sortOrder', Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
              />
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="app-staging"
              className="mt-0.5"
              checked={fields.stagingSite}
              onCheckedChange={(c) => set('stagingSite', c === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="app-staging">Staging site</Label>
              <p className="text-xs text-slate-500">
                A second copy of an app, for testing. Its tile goes under Staging Sites, below the other apps.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {editing ? 'Save' : 'Add app'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
